/**
 * C9 -- the Engine being off is stated once, with the manual path.
 *
 * Before this, a preparer discovered it by pressing a button: Test of
 * Design failed with a toast, the analyser showed "Set
 * MARSAD_AI_PROVIDER + the matching MARSAD_AI_* env vars and restart
 * the backend" -- an instruction for somebody with shell access to the
 * deployment, shown to somebody who has none -- and the regulatory
 * change screen offered no alternative at all.
 *
 * So: say it before the button, and say what to do instead. The
 * `manual` line is per surface because the answer is: on the assessment
 * grid you grade the control yourself, on the letter page you write the
 * observations yourself, and those are different sentences.
 *
 * Renders NOTHING when the Engine is on. A banner that persists once
 * the thing it warns about is fixed teaches people to ignore banners.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";

interface EngineAvailability {
  configured: boolean;
}

/** Shared key: every surface asks the same question, once. */
export const engineAvailabilityKey = ["marsad-engine", "availability"] as const;

export function useEngineAvailability() {
  return useQuery<EngineAvailability>({
    queryKey: engineAvailabilityKey,
    queryFn: () => api.get<EngineAvailability>("/v1/marsad-engine/availability"),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * @param manualKey i18n key for the sentence saying what to do
 *   instead. A KEY rather than a string, so a call site does not have
 *   to reach for `t` -- several of these surfaces have none.
 */
export function EngineOffBanner({ manualKey }: { manualKey: string }) {
  const { t } = useTranslation();
  const q = useEngineAvailability();
  // While we do not know, say nothing. Guessing "off" would put a
  // warning in front of an operator whose Engine is fine.
  if (q.isLoading || q.isError || q.data?.configured !== false) return null;
  return (
    <p
      data-testid="engine-off-banner"
      className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <span className="font-semibold">{t("engineOff.heading")}</span> {t(manualKey)}
    </p>
  );
}
